export interface CategoryEntry {
  id: string;
  name: string;
  subCategories: string[];
}

const STORAGE_KEY = 'eliquidesud_categories';

const DEFAULTS: CategoryEntry[] = [
  { id: 'e-liquide',               name: 'E-Liquide',               subCategories: ['200ml', '100ml', '50ml', '10ml'] },
  { id: 'diy',                     name: 'DIY',                     subCategories: ['Arôme', 'Nicotine', 'Base'] },
  { id: 'accessoires',             name: 'Accessoires',             subCategories: ['Accu', 'Résistance', 'Verre'] },
  { id: 'cigarette-electronique',  name: 'Cigarette électronique',  subCategories: ['Box', 'Kit', 'Atomiseurs'] },
];

export function getCategories(): CategoryEntry[] {
  try {
    const raw = localStorage.getItem(STORAGE_KEY);
    if (!raw) return DEFAULTS;
    const parsed = JSON.parse(raw) as CategoryEntry[];
    return parsed.length > 0 ? parsed : DEFAULTS;
  } catch {
    return DEFAULTS;
  }
}

export function saveCategories(categories: CategoryEntry[]): void {
  localStorage.setItem(STORAGE_KEY, JSON.stringify(categories));
}

export function getCategoryNames(): string[] {
  return getCategories().map((c) => c.name);
}

export function getSubCategories(categoryName: string): string[] {
  const cat = getCategories().find(
    (c) => c.name.toLowerCase() === categoryName.toLowerCase()
  );
  return cat?.subCategories ?? [];
}

// ── Règles d'auto-catégorisation ──────────────────────────────────────────────

export interface AutoRule {
  id: string;
  keyword: string;       // mot-clé unique (insensible à la casse)
  category: string;
  subCategory: string;
}

export interface RulePreset {
  id: string;
  name: string;
  description: string;
  rules: Omit<AutoRule, 'id'>[];
}

export interface RuleSet {
  id: string;
  name: string;
  description: string;
  presetIds: string[];
}

const RULES_KEY = 'eliquidesud_auto_rules';

export const BUILTIN_PRESETS: RulePreset[] = [
  {
    id: 'preset-eliquide',
    name: 'E-Liquide (volumes)',
    description: 'Détecte 10ml, 50ml, 100ml, 200ml',
    rules: [
      { keyword: '200ml', category: 'E-Liquide', subCategory: '200ml' },
      { keyword: '200 ml', category: 'E-Liquide', subCategory: '200ml' },
      { keyword: '100ml', category: 'E-Liquide', subCategory: '100ml' },
      { keyword: '100 ml', category: 'E-Liquide', subCategory: '100ml' },
      { keyword: '50ml',  category: 'E-Liquide', subCategory: '50ml'  },
      { keyword: '50 ml', category: 'E-Liquide', subCategory: '50ml'  },
      { keyword: '10ml',  category: 'E-Liquide', subCategory: '10ml'  },
      { keyword: '10 ml', category: 'E-Liquide', subCategory: '10ml'  },
    ],
  },
  {
    id: 'preset-diy',
    name: 'DIY',
    description: 'Détecte arômes, nicotine, boosters',
    rules: [
      { keyword: 'arôme',   category: 'DIY', subCategory: 'Arôme' },
      { keyword: 'arome',   category: 'DIY', subCategory: 'Arôme' },
      { keyword: 'nicotine',category: 'DIY', subCategory: 'Nicotine' },
      { keyword: 'booster', category: 'DIY', subCategory: 'Nicotine' },
      { keyword: 'base',    category: 'DIY', subCategory: 'Base' },
    ],
  },
  {
    id: 'preset-accessoires',
    name: 'Accessoires',
    description: 'Détecte accus, résistances, verres',
    rules: [
      { keyword: 'accu',        category: 'Accessoires', subCategory: 'Accu' },
      { keyword: 'batterie',    category: 'Accessoires', subCategory: 'Accu' },
      { keyword: 'résistance',  category: 'Accessoires', subCategory: 'Résistance' },
      { keyword: 'resistance',  category: 'Accessoires', subCategory: 'Résistance' },
      { keyword: 'coil',        category: 'Accessoires', subCategory: 'Résistance' },
      { keyword: 'verre',       category: 'Accessoires', subCategory: 'Verre' },
      { keyword: 'pyrex',       category: 'Accessoires', subCategory: 'Verre' },
    ],
  },
  {
    id: 'preset-cig-elec',
    name: 'Cigarette électronique',
    description: 'Détecte box, kits, atomiseurs',
    rules: [
      { keyword: 'box mod',       category: 'Cigarette électronique', subCategory: 'Box' },
      { keyword: 'box',           category: 'Cigarette électronique', subCategory: 'Box' },
      { keyword: 'kit',           category: 'Cigarette électronique', subCategory: 'Kit' },
      { keyword: 'atomiseur',     category: 'Cigarette électronique', subCategory: 'Atomiseurs' },
      { keyword: 'clearomiseur',  category: 'Cigarette électronique', subCategory: 'Atomiseurs' },
    ],
  },
];

export const BUILTIN_SETS: RuleSet[] = [
  {
    id: 'set-complet',
    name: 'Pack complet vape',
    description: 'Installe tous les présets en une fois',
    presetIds: ['preset-eliquide', 'preset-diy', 'preset-accessoires', 'preset-cig-elec'],
  },
  {
    id: 'set-liquides',
    name: 'Pack liquides',
    description: 'E-Liquide + DIY uniquement',
    presetIds: ['preset-eliquide', 'preset-diy'],
  },
  {
    id: 'set-materiel',
    name: 'Pack matériel',
    description: 'Accessoires + Cigarette électronique',
    presetIds: ['preset-accessoires', 'preset-cig-elec'],
  },
];

export function getRules(): AutoRule[] {
  try {
    const raw = localStorage.getItem(RULES_KEY);
    if (!raw) return [];
    return JSON.parse(raw) as AutoRule[];
  } catch {
    return [];
  }
}

export function saveRules(rules: AutoRule[]): void {
  localStorage.setItem(RULES_KEY, JSON.stringify(rules));
}

export function addRule(rule: Omit<AutoRule, 'id'>): void {
  const rules = getRules();
  rules.push({ ...rule, id: `rule-${Date.now()}` });
  saveRules(rules);
}

export function deleteRule(id: string): void {
  saveRules(getRules().filter((r) => r.id !== id));
}

export function installPreset(presetId: string): number {
  const preset = BUILTIN_PRESETS.find((p) => p.id === presetId);
  if (!preset) return 0;
  const existing = getRules();
  const existingKeywords = new Set(existing.map((r) => r.keyword.toLowerCase()));
  const toAdd = preset.rules
    .filter((r) => !existingKeywords.has(r.keyword.toLowerCase()))
    .map((r) => ({ ...r, id: `rule-${Date.now()}-${Math.random()}` }));
  saveRules([...existing, ...toAdd]);
  return toAdd.length;
}

export function autoCategorize(productName: string): { category: string; subCategory: string } | null {
  const lower = productName.toLowerCase();
  for (const rule of getRules()) {
    if (lower.includes(rule.keyword.toLowerCase())) {
      return { category: rule.category, subCategory: rule.subCategory };
    }
  }
  return null;
}
